import { type ComponentPropsWithRef, type HTMLAttributes } from "react";
import {
  Root as DialogPrimitiveRoot,
  Trigger as DialogPrimitiveTrigger,
  Portal as DialogPortal,
  Close as DialogPrimitiveClose,
  Overlay as DialogPrimitiveOverlay,
  Content as DialogPrimitiveContent,
  Title as DialogPrimitiveTitle,
  Description as DialogPrimitiveDescription,
} from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { PaperFloating } from "@/components/atoms/paperFloating";
import { cn } from "@/lib/utils";

const Dialog = DialogPrimitiveRoot;

const DialogTrigger = DialogPrimitiveTrigger;

const DialogOverlay = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof DialogPrimitiveOverlay>) => (
  <DialogPrimitiveOverlay
    ref={ref}
    className={cn(
      `
        fixed inset-0 z-50 bg-black/80 backdrop-blur-[6px]
        data-[state=closed]:animate-out data-[state=closed]:fade-out-0
        data-[state=open]:animate-in data-[state=open]:fade-in-0
      `,
      className,
    )}
    {...props}
  />
);
DialogOverlay.displayName = DialogPrimitiveOverlay.displayName;

const DialogContent = ({
  className,
  children,
  surface = "default",
  ref,
  ...props
}: ComponentPropsWithRef<typeof DialogPrimitiveContent> & {
  surface?: "default" | "console";
}) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitiveContent asChild {...props}>
      <PaperFloating
        ref={ref}
        surface={surface}
        data-dialog={surface === "console" ? "console" : undefined}
        className={cn(
          `
            fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg
            translate-[-50%] gap-4 bg-background p-6 duration-200
            data-[state=closed]:animate-out data-[state=closed]:fade-out-0
            data-[state=closed]:slide-out-to-top-[48%]
            data-[state=closed]:zoom-out-95
            data-[state=open]:animate-in data-[state=open]:fade-in-0
            data-[state=open]:slide-in-from-top-[48%]
            data-[state=open]:zoom-in-95
            sm:rounded-sm
          `,
          surface === "console" &&
            `
              **:data-[heading=panel-title]:mb-0
              **:data-[text=body]:mt-0
            `,
          className,
        )}
      >
        {children}
        <DialogPrimitiveClose
          className="
            absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background
            transition-opacity
            hover:opacity-100
            focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none
            disabled:pointer-events-none
            data-[state=open]:bg-accent data-[state=open]:text-accent-foreground
          "
        >
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitiveClose>
      </PaperFloating>
    </DialogPrimitiveContent>
  </DialogPortal>
);
DialogContent.displayName = DialogPrimitiveContent.displayName;

const DialogHeader = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col space-y-1.5 text-center
        sm:text-left
      `,
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      `
        flex flex-col-reverse
        sm:flex-row sm:justify-end sm:space-x-2
      `,
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof DialogPrimitiveTitle>) => (
  <DialogPrimitiveTitle
    ref={ref}
    className={cn(
      "text-lg leading-none font-semibold tracking-tight",
      className,
    )}
    {...props}
  />
);
DialogTitle.displayName = DialogPrimitiveTitle.displayName;

const DialogDescription = ({
  className,
  ref,
  ...props
}: ComponentPropsWithRef<typeof DialogPrimitiveDescription>) => (
  <DialogPrimitiveDescription
    ref={ref}
    className={cn("text-sm text-foreground/80", className)}
    {...props}
  />
);
DialogDescription.displayName = DialogPrimitiveDescription.displayName;

export {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
